import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { DEV_AUTH_URL } from "../../config";
import { CustomToast } from "../../util/custom-toast/CustomToast";
import "./styles.scss";

/**
 * Custom captcha (matches developer-auth GET /api/v1/captcha/generate).
 * Parent receives { userInput, encryptedData } via handleCaptchaChange.
 */
const Captcha = forwardRef(({ value, handleCaptchaChange }, ref) => {
  const [captchaImageSrc, setCaptchaImageSrc] = useState("");
  const [captchaEncryptedData, setCaptchaEncryptedData] = useState(null);

  const getCaptcha = async () => {
    const base = DEV_AUTH_URL || "";
    const url = `${base.replace(/\/?$/, "/")}captcha/generate`;
    try {
      // Clear previous captcha when loading new one
      setCaptchaImageSrc("");
      setCaptchaEncryptedData(null);
      handleCaptchaChange({ userInput: "", encryptedData: null });
      
      const response = await fetch(url, { method: "GET" });
      if (!response.ok) {
        throw new Error("Could not load captcha. Please try again.");
      }
      const captchaData = await response.json();
      const { captchaImage, encryptedData } = captchaData.data || {};
      if (!captchaImage || !encryptedData) {
        throw new Error("Invalid captcha response from server.");
      }
      setCaptchaImageSrc(captchaImage);
      setCaptchaEncryptedData(encryptedData);
      handleCaptchaChange({ userInput: "", encryptedData });
    } catch (error) {
      CustomToast(error?.message || "Captcha load failed");
    }
  };

  useImperativeHandle(ref, () => ({
    regenerateCaptcha() {
      getCaptcha();
    },
  }));

  const handleChange = (event) => {
    const { value: inputVal } = event.target;
    handleCaptchaChange({
      userInput: inputVal,
      encryptedData: captchaEncryptedData,
    });
  };

  useEffect(() => {
    getCaptcha();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dev-captcha-wrap">
      <div className="dev-captcha-row">
        <div className="dev-captcha-img">
          {captchaImageSrc ? (
            <img src={captchaImageSrc} alt="captcha" />
          ) : (
            <div style={{ padding: '10px', color: '#999' }}>Loading captcha...</div>
          )}
        </div>
        <button
          type="button"
          className="dev-captcha-reload"
          onClick={getCaptcha}
          aria-label="Reload captcha"
          title="Click to reload captcha if unclear"
        >
          ↻
        </button>
      </div>
      <input
        className="dev-captcha-input"
        type="text"
        placeholder="Enter the text shown above (case-sensitive)"
        name="userInput"
        value={value || ""}
        onChange={handleChange}
        autoComplete="off"
      />
      {value && value.length > 0 && (
        <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
          You entered: <strong>{value}</strong> ({value.length} characters)
        </div>
      )}
    </div>
  );
});

Captcha.displayName = "Captcha";

export default Captcha;
